import React, { useState, useEffect, useRef } from "react";
import { message, Spin, Select } from "antd";
import {
  FaNotesMedical,
  FaHeartbeat,
  FaLungs,
  FaBrain,
  FaFlask,
  FaSave,
  FaSyncAlt,
  FaCalendarAlt,
  FaStethoscope,
  FaHistory,
  FaClipboardList,
} from "react-icons/fa";
import { FiPlus, FiTrash2, FiX } from "react-icons/fi";
import { MdOutlineSick, MdLocalHospital } from "react-icons/md";
import { GiStomach } from "react-icons/gi";
import CaseSheetService from "../../../../Services/CaseSheetService";
import ConsultationService from "../../../../Services/ConsultationService";

export default function PatientCaseSheet({
  patientDetails,
  patientId: propPatientId,
  admissionId,
  admission,
  onClose,
  onSave,
}) {
  const inputStyle =
    "w-full min-w-0 border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-[#008f9f] focus:border-[#008f9f] bg-white transition-all text-gray-700";

  const textareaStyle =
    "w-full min-w-0 border border-gray-200 rounded-lg px-3 py-2 text-xs resize-y outline-none focus:ring-1 focus:ring-[#008f9f] focus:border-[#008f9f] bg-white transition-all text-gray-700";

  const sectionHeader =
    "flex items-center gap-2 text-sm font-bold text-gray-800 pb-2 mb-3 border-b border-gray-100";

  const fieldLabel = "block text-xs font-medium text-gray-600 mb-1.5";

  const getLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const today = getLocalDate(new Date());

  // Date formatting for GET API: MM/DD/YYYY (e.g. 10/24/2026)
  const formatToFilterDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${month}/${day}/${year}`;
    }
    const d = new Date(dateStr);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  // Active user / nurse context
  const loginObj = (() => {
    try {
      return (
        sessionStorage.getItem("login_result") ||
        localStorage.getItem("login_result")
      );
    } catch {
      return null;
    }
  })();
  const userData = loginObj ? JSON.parse(loginObj) : null;
  const currentUserId = userData?.user?.userId || "DZU5891001";

  // Patient resolution
  const resolvedPatientId =
    propPatientId ||
    patientDetails?.patientId ||
    patientDetails?.patientCode ||
    patientDetails?.uhid ||
    "";

  const resolvedAdmissionNo =
    admission?.admissionNo ||
    admission?.admissionNumber ||
    admission?.admissionId ||
    admissionId ||
    patientDetails?.admissionNo ||
    "";

  const patientName = patientDetails?.firstName
    ? `${patientDetails.firstName} ${patientDetails?.lastName || ""}`.trim()
    : patientDetails?.patientName || "Patient";

  // State
  const [patientId, setPatientId] = useState(resolvedPatientId);
  const [admissionNo, setAdmissionNo] = useState(resolvedAdmissionNo);
  const [status, setStatus] = useState("DRAFT");
  const [selectedDate, setSelectedDate] = useState(() => today);
  const [weekOffset, setWeekOffset] = useState(0);
  const [activeSection, setActiveSection] = useState("all");

  const [isDateLoading, setIsDateLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [existingRecord, setExistingRecord] = useState(null);

  // Sync state if props change after async fetch
  useEffect(() => {
    if (resolvedPatientId && resolvedPatientId !== patientId) {
      setPatientId(resolvedPatientId);
    }
  }, [resolvedPatientId]);

  useEffect(() => {
    if (resolvedAdmissionNo && resolvedAdmissionNo !== admissionNo) {
      setAdmissionNo(resolvedAdmissionNo);
    }
  }, [resolvedAdmissionNo]);

  // Section Refs for quick scroll
  const sectionRefs = {
    complaints: useRef(null),
    diagnosis: useRef(null),
    history: useRef(null),
    vitals: useRef(null),
    systems: useRef(null),
    investigations: useRef(null),
  };

  const scrollToSection = (sectionKey) => {
    setActiveSection(sectionKey);
    if (sectionKey === "all") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const ref = sectionRefs[sectionKey];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // 1. Complaints
  const [complaints, setComplaints] = useState({
    presentingComplaint1: "",
    presentingComplaint2: "",
    presentingComplaint3: "",
    associatedComplaint1: "",
    associatedComplaint2: "",
    associatedComplaint3: "",
  });

  // 2. Provisional Diagnosis (Multiple entries + ICD-10 Master Data)
  const [diagnosisList, setDiagnosisList] = useState([]);
  const [selectedType, setSelectedType] = useState("PRIMARY");
  const [selectedCode, setSelectedCode] = useState(null);
  const [diagnosticList, setDiagnosticList] = useState([]);
  const [diagnosisNotesInput, setDiagnosisNotesInput] = useState("");
  const [specialtyCode, setSpecialtyCode] = useState(
    patientDetails?.specialityCode || "GENMED"
  );
  const [packageCode, setPackageCode] = useState("PKG001");

  const handleIcdDrescriptionSearch = async (value) => {
    if (value && value.length > 2) {
      let payload = {
        description: value,
        pageSize: 50,
        status: 1,
      };

      try {
        let result = await ConsultationService.ICDData(payload);
        setDiagnosticList(result.data || []);
      } catch (error) {
        setDiagnosticList([]);
      }
    }
  };

  const handleIcdCodeSearch = async (value) => {
    if (value && value.length > 2) {
      let payload = {
        code: value,
        pageSize: 50,
        status: 1,
      };

      try {
        let result = await ConsultationService.ICDData(payload);
        setDiagnosticList(result.data || []);
      } catch (error) {
        setDiagnosticList([]);
      }
    }
  };

  const handleAddDiagnosis = () => {
    if (!selectedCode && !diagnosisNotesInput.trim()) {
      message.warning("Please select a disease condition or enter diagnosis notes.");
      return;
    }

    const desc = selectedCode?.description || diagnosisNotesInput.trim();
    const code = selectedCode?.code || "";
    const diagType =
      selectedType === "SECONDARY" || selectedType === "Secondary"
        ? "Secondary"
        : "Primary";

    const newEntry = {
      id: 0,
      caseSheetId: existingRecord?.id || "",
      diagnosisType: diagType,
      description: desc,
      diagnosisDesc: desc,
      diagnosisCode: code,
      primaryDiagnosis: diagType === "Primary" ? desc : "",
      secondaryDiagnosis: diagType === "Secondary" ? desc : "",
      specialtyCode: specialtyCode || patientDetails?.specialityCode || "GENMED",
      packageCode: packageCode || "",
      diagnosisNotes: diagnosisNotesInput?.trim() || "",
      createdBy: String(currentUserId || "").trim(),
      createdDate: new Date().toISOString(),
      modifiedBy: String(currentUserId || "").trim(),
      modifiedDate: new Date().toISOString(),
      status: 1,
    };

    setDiagnosisList((prev) => [...prev, newEntry]);
    setSelectedCode(null);
    setDiagnosisNotesInput("");
  };

  const handleRemoveDiagnosis = (index) => {
    setDiagnosisList((prev) => prev.filter((_, idx) => idx !== index));
  };

  // 3. History
  const [history, setHistory] = useState({
    historyOfPresentIllness: "",
    historyOfPastIllness: "",
    familyHistory: "",
    personalHistory: "",
    treatmentHistory: "",
    socialOccupationalHistory: "",
  });

  // 4. Physical Examination & Vitals
  const [examination, setExamination] = useState({
    pulse: "",
    respiration: "",
    temperature: "",
    bloodPressure: "",
    weight: "",
    height: "",
    bmi: "",
    edema: "",
    scalp: "",
    eyes: "",
    nose: "",
    oralCavity: "",
    tongue: "",
    neckGlands: "",
    nails: "",
    lymphNodes: "",
    pallorAnemia: "",
    cyanosis: "",
    extremities: "",
    jaundice: "",
  });

  // 5. Respiratory
  const [respiratory, setRespiratory] = useState({
    respiratoryRateRhythm: "",
    shapeSymmetryChest: "",
    nose: "",
    crepitations: "",
    wheezing: "",
    pleuralRub: "",
  });

  // 6. Cardiovascular
  const [cardiovascular, setCardiovascular] = useState({
    pulseRate: "",
    rhythm: "",
    volume: "",
    bloodPressure: "",
    apexBeat: "",
    pedalEdema: "",
    auscultation: "",
  });

  // 7. Gastrointestinal
  const [gastrointestinal, setGastrointestinal] = useState({
    lips: "",
    teeth: "",
    tongue: "",
    tonsils: "",
    abdomenShape: "",
    abdominalOrgans: "",
    tenderness: "",
    liver: "",
    spleen: "",
    bowelSounds: "",
  });

  // 8. CNS
  const [cns, setCns] = useState({
    mentalStatus: "",
    memory: "",
    orientation: "",
    speech: "",
    generalBehavior: "",
    gait: "",
    cranialNerveExamination: "",
    motorSensoryFindings: "",
  });

  // 9. Investigation
  const [investigation, setInvestigation] = useState({
    blood: "",
    urine: "",
    sputum: "",
    ecg: "",
    xray: "",
    ct: "",
    mri: "",
    others: "",
  });

  // Helper to extract clean vital number from string e.g. "72 bpm" -> "72"
  const cleanVital = (val) => {
    if (!val) return "";
    return String(val).replace(/[^\d./]/g, "").trim();
  };

  // Populate data
  const applyCaseSheetData = (data) => {
    if (!data) return;
    setExistingRecord(data);
    setIsUpdateMode(true);

    if (data.status) setStatus(data.status);
    if (data.admissionNo) setAdmissionNo(data.admissionNo);
    if (data.patientId) setPatientId(data.patientId);

    if (data.patientCaseSheetComplaint) {
      setComplaints({
        presentingComplaint1: data.patientCaseSheetComplaint.presentingComplaint1 || "",
        presentingComplaint2: data.patientCaseSheetComplaint.presentingComplaint2 || "",
        presentingComplaint3: data.patientCaseSheetComplaint.presentingComplaint3 || "",
        associatedComplaint1: data.patientCaseSheetComplaint.associatedComplaint1 || "",
        associatedComplaint2: data.patientCaseSheetComplaint.associatedComplaint2 || "",
        associatedComplaint3: data.patientCaseSheetComplaint.associatedComplaint3 || "",
      });
    }

    if (data.patientCaseSheetHistory) {
      setHistory({
        historyOfPresentIllness: data.patientCaseSheetHistory.historyOfPresentIllness || "",
        historyOfPastIllness: data.patientCaseSheetHistory.historyOfPastIllness || "",
        familyHistory: data.patientCaseSheetHistory.familyHistory || "",
        personalHistory: data.patientCaseSheetHistory.personalHistory || "",
        treatmentHistory: data.patientCaseSheetHistory.treatmentHistory || "",
        socialOccupationalHistory: data.patientCaseSheetHistory.socialOccupationalHistory || "",
      });
    }

    if (data.patientCaseSheetExamination) {
      const exam = data.patientCaseSheetExamination;
      setExamination({
        pulse: cleanVital(exam.pulse),
        respiration: cleanVital(exam.respiration),
        temperature: cleanVital(exam.temperature),
        bloodPressure: cleanVital(exam.bloodPressure),
        weight: cleanVital(exam.weight),
        height: cleanVital(exam.height),
        bmi: exam.bmi ? String(exam.bmi) : "",
        edema: exam.edema || "",
        scalp: exam.scalp || "",
        eyes: exam.eyes || "",
        nose: exam.nose || "",
        oralCavity: exam.oralCavity || "",
        tongue: exam.tongue || "",
        neckGlands: exam.neckGlands || "",
        nails: exam.nails || "",
        lymphNodes: exam.lymphNodes || "",
        pallorAnemia: exam.pallorAnemia || "",
        cyanosis: exam.cyanosis || "",
        extremities: exam.extremities || "",
        jaundice: exam.jaundice || "",
      });
    }

    if (data.patientCaseSheetRespiratory) {
      setRespiratory({
        respiratoryRateRhythm: data.patientCaseSheetRespiratory.respiratoryRateRhythm || "",
        shapeSymmetryChest: data.patientCaseSheetRespiratory.shapeSymmetryChest || "",
        nose: data.patientCaseSheetRespiratory.nose || "",
        crepitations: data.patientCaseSheetRespiratory.crepitations || "",
        wheezing: data.patientCaseSheetRespiratory.wheezing || "",
        pleuralRub: data.patientCaseSheetRespiratory.pleuralRub || "",
      });
    }

    if (data.patientCaseSheetCardiovascular) {
      setCardiovascular({
        pulseRate: cleanVital(data.patientCaseSheetCardiovascular.pulseRate),
        rhythm: data.patientCaseSheetCardiovascular.rhythm || "",
        volume: data.patientCaseSheetCardiovascular.volume || "",
        bloodPressure: cleanVital(data.patientCaseSheetCardiovascular.bloodPressure),
        apexBeat: data.patientCaseSheetCardiovascular.apexBeat || "",
        pedalEdema: data.patientCaseSheetCardiovascular.pedalEdema || "",
        auscultation: data.patientCaseSheetCardiovascular.auscultation || "",
      });
    }

    if (data.patientCaseSheetGastrointestinal) {
      setGastrointestinal({
        lips: data.patientCaseSheetGastrointestinal.lips || "",
        teeth: data.patientCaseSheetGastrointestinal.teeth || "",
        tongue: data.patientCaseSheetGastrointestinal.tongue || "",
        tonsils: data.patientCaseSheetGastrointestinal.tonsils || "",
        abdomenShape: data.patientCaseSheetGastrointestinal.abdomenShape || "",
        abdominalOrgans: data.patientCaseSheetGastrointestinal.abdominalOrgans || "",
        tenderness: data.patientCaseSheetGastrointestinal.tenderness || "",
        liver: data.patientCaseSheetGastrointestinal.liver || "",
        spleen: data.patientCaseSheetGastrointestinal.spleen || "",
        bowelSounds: data.patientCaseSheetGastrointestinal.bowelSounds || "",
      });
    }

    if (data.patientCaseSheetCns) {
      setCns({
        mentalStatus: data.patientCaseSheetCns.mentalStatus || "",
        memory: data.patientCaseSheetCns.memory || "",
        orientation: data.patientCaseSheetCns.orientation || "",
        speech: data.patientCaseSheetCns.speech || "",
        generalBehavior: data.patientCaseSheetCns.generalBehavior || "",
        gait: data.patientCaseSheetCns.gait || "",
        cranialNerveExamination: data.patientCaseSheetCns.cranialNerveExamination || "",
        motorSensoryFindings: data.patientCaseSheetCns.motorSensoryFindings || "",
      });
    }

    if (data.patientCaseSheetInvestigation) {
      setInvestigation({
        blood: data.patientCaseSheetInvestigation.blood || "",
        urine: data.patientCaseSheetInvestigation.urine || "",
        sputum: data.patientCaseSheetInvestigation.sputum || "",
        ecg: data.patientCaseSheetInvestigation.ecg || "",
        xray: data.patientCaseSheetInvestigation.xray || "",
        ct: data.patientCaseSheetInvestigation.ct || "",
        mri: data.patientCaseSheetInvestigation.mri || "",
        others: data.patientCaseSheetInvestigation.others || "",
      });
    }

    if (data.patientCaseSheetDiagnosis) {
      let rawList = [];
      if (Array.isArray(data.patientCaseSheetDiagnosis)) {
        rawList = data.patientCaseSheetDiagnosis;
      } else if (typeof data.patientCaseSheetDiagnosis === "object") {
        const single = data.patientCaseSheetDiagnosis;
        if (single.primaryDiagnosis) {
          rawList.push({
            ...single,
            diagnosisType: "PRIMARY",
            diagnosisDesc: single.primaryDiagnosis,
          });
        }
        if (single.secondaryDiagnosis) {
          rawList.push({
            ...single,
            diagnosisType: "SECONDARY",
            diagnosisDesc: single.secondaryDiagnosis,
          });
        }
        if (
          !single.primaryDiagnosis &&
          !single.secondaryDiagnosis &&
          (single.diagnosisDesc || single.diagnosisNotes)
        ) {
          rawList.push(single);
        }
      }

      const formattedList = rawList.map((item) => {
        const diagType = item.diagnosisType
          ? item.diagnosisType.toUpperCase() === "SECONDARY"
            ? "Secondary"
            : "Primary"
          : item.secondaryDiagnosis
          ? "Secondary"
          : "Primary";
        const desc =
          item.description ||
          item.diagnosisDesc ||
          item.primaryDiagnosis ||
          item.secondaryDiagnosis ||
          item.diagnosisNotes ||
          "";
        const code =
          item.diagnosisCode ||
          (item.diagnosisNotes &&
          /^[A-Z]\d{2}(\.\d+)?$/i.test(item.diagnosisNotes.trim())
            ? item.diagnosisNotes.trim()
            : "");

        return {
          id: item.id || 0,
          caseSheetId: item.caseSheetId || data.id || 0,
          diagnosisType: diagType,
          description: desc,
          diagnosisDesc: desc,
          diagnosisCode: code,
          primaryDiagnosis:
            item.primaryDiagnosis || (diagType === "Primary" ? desc : ""),
          secondaryDiagnosis:
            item.secondaryDiagnosis || (diagType === "Secondary" ? desc : ""),
          specialtyCode: item.specialtyCode || specialtyCode || "GENMED",
          packageCode: item.packageCode || packageCode || "",
          diagnosisNotes: item.diagnosisNotes || "",
          createdBy: item.createdBy || currentUserId,
          createdDate: item.createdDate || new Date().toISOString(),
          modifiedBy: item.modifiedBy || currentUserId,
          modifiedDate: item.modifiedDate || new Date().toISOString(),
          status: item.status !== undefined ? item.status : 1,
        };
      });

      setDiagnosisList(formattedList);

      if (rawList.length > 0) {
        if (rawList[0].specialtyCode) setSpecialtyCode(rawList[0].specialtyCode);
        if (rawList[0].packageCode) setPackageCode(rawList[0].packageCode);
      }
    } else {
      setDiagnosisList([]);
    }
  };

  // Reset form
  const resetFormForDate = () => {
    setExistingRecord(null);
    setIsUpdateMode(false);
    setStatus("DRAFT");

    setComplaints({
      presentingComplaint1: "",
      presentingComplaint2: "",
      presentingComplaint3: "",
      associatedComplaint1: "",
      associatedComplaint2: "",
      associatedComplaint3: "",
    });

    setDiagnosisList([]);
    setSelectedType("PRIMARY");
    setSelectedCode(null);
    setDiagnosticList([]);
    setDiagnosisNotesInput("");
    setSpecialtyCode(patientDetails?.specialityCode || "GENMED");
    setPackageCode("PKG001");

    setHistory({
      historyOfPresentIllness: "",
      historyOfPastIllness: "",
      familyHistory: "",
      personalHistory: "",
      treatmentHistory: "",
      socialOccupationalHistory: "",
    });

    setExamination({
      pulse: "",
      respiration: "",
      temperature: "",
      bloodPressure: "",
      weight: "",
      height: "",
      bmi: "",
      edema: "",
      scalp: "",
      eyes: "",
      nose: "",
      oralCavity: "",
      tongue: "",
      neckGlands: "",
      nails: "",
      lymphNodes: "",
      pallorAnemia: "",
      cyanosis: "",
      extremities: "",
      jaundice: "",
    });

    setRespiratory({
      respiratoryRateRhythm: "",
      shapeSymmetryChest: "",
      nose: "",
      crepitations: "",
      wheezing: "",
      pleuralRub: "",
    });

    setCardiovascular({
      pulseRate: "",
      rhythm: "",
      volume: "",
      bloodPressure: "",
      apexBeat: "",
      pedalEdema: "",
      auscultation: "",
    });

    setGastrointestinal({
      lips: "",
      teeth: "",
      tongue: "",
      tonsils: "",
      abdomenShape: "",
      abdominalOrgans: "",
      tenderness: "",
      liver: "",
      spleen: "",
      bowelSounds: "",
    });

    setCns({
      mentalStatus: "",
      memory: "",
      orientation: "",
      speech: "",
      generalBehavior: "",
      gait: "",
      cranialNerveExamination: "",
      motorSensoryFindings: "",
    });

    setInvestigation({
      blood: "",
      urine: "",
      sputum: "",
      ecg: "",
      xray: "",
      ct: "",
      mri: "",
      others: "",
    });
  };

  // Fetch record for selected date
  useEffect(() => {
    let isCurrent = true;

    const fetchCaseSheetForDate = async () => {
      if (!patientId) return;

      const formattedFilterDate = formatToFilterDate(selectedDate);
      setIsDateLoading(true);

      try {
        const response = await CaseSheetService.getPatientCaseSheetFilter({
          patientId: patientId,
          fromDate: formattedFilterDate,
          toDate: formattedFilterDate,
        });

        if (!isCurrent) return;

        let record = null;
        const resData = response?.data;
        if (Array.isArray(resData) && resData.length > 0) {
          record = resData[0];
        } else if (Array.isArray(resData?.data) && resData.data.length > 0) {
          record = resData.data[0];
        } else if (Array.isArray(resData?.list) && resData.list.length > 0) {
          record = resData.list[0];
        } else if (resData?.patientId || resData?.patientCaseSheetComplaint) {
          record = resData;
        }

        if (record) {
          applyCaseSheetData(record);
        } else {
          resetFormForDate();
        }
      } catch (err) {
        console.warn("No existing case sheet for selected date:", err);
        if (isCurrent) {
          resetFormForDate();
        }
      } finally {
        if (isCurrent) {
          setIsDateLoading(false);
        }
      }
    };

    fetchCaseSheetForDate();

    return () => {
      isCurrent = false;
    };
  }, [selectedDate, patientId]);

  // Update examination field with auto-BMI calculation
  const updateExaminationField = (field, value) => {
    setExamination((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "weight" || field === "height") {
        const weightVal = parseFloat(field === "weight" ? value : prev.weight);
        const heightVal = parseFloat(field === "height" ? value : prev.height);
        if (weightVal > 0 && heightVal > 0) {
          const heightM = heightVal / 100;
          next.bmi = (weightVal / (heightM * heightM)).toFixed(1);
        } else {
          next.bmi = "";
        }
      }
      return next;
    });
  };

  // Week calendar calculation
  const getWeekDates = () => {
    const current = new Date(`${today}T12:00:00`);
    current.setDate(current.getDate() + weekOffset * 7);

    const day = current.getDay();
    current.setDate(current.getDate() - day + 1);

    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(current);
      date.setDate(current.getDate() + index);

      return {
        value: getLocalDate(date),
        day: date.toLocaleDateString("en-US", { weekday: "short" }),
        number: date.getDate(),
        month: date.toLocaleDateString("en-US", { month: "short" }),
      };
    });
  };

  const weekDates = getWeekDates();

  const selectedDateLabel = new Date(`${selectedDate}T12:00:00`).toLocaleDateString(
    "en-IN",
    {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    }
  );

  // Vital formatter for API payload
  const formatVital = (val, unit) => {
    if (val === undefined || val === null) return "";
    const str = String(val).trim();
    if (!str) return "";
    if (unit === "bpm" && /^\d+$/.test(str)) return `${str} bpm`;
    if (unit === "/min" && /^\d+$/.test(str)) return `${str}/min`;
    if (unit === "C" && /^[\d.]+$/.test(str)) return `${str} C`;
    if (unit === "mmHg" && /^\d+\/\d+$/.test(str)) return `${str} mmHg`;
    if (unit === "kg" && /^[\d.]+$/.test(str)) return `${str} kg`;
    if (unit === "cm" && /^[\d.]+$/.test(str)) return `${str} cm`;
    return str;
  };

  // Build Payload
  const buildSavePayload = (targetStatus = status) => {
    return {
      ...(existingRecord || {}),
      ...(admissionNo ? { admissionNo } : {}),
      patientId: String(patientId || "").trim(),
      status: targetStatus || "DRAFT",
      createdBy: String(existingRecord?.createdBy || currentUserId || "").trim(),
      updatedBy: String(currentUserId || "").trim(),
      patientCaseSheetComplaint: {
        ...(existingRecord?.patientCaseSheetComplaint || {}),
        presentingComplaint1: complaints.presentingComplaint1 || "",
        presentingComplaint2: complaints.presentingComplaint2 || "",
        presentingComplaint3: complaints.presentingComplaint3 || "",
        associatedComplaint1: complaints.associatedComplaint1 || "",
        associatedComplaint2: complaints.associatedComplaint2 || "",
        associatedComplaint3: complaints.associatedComplaint3 || "",
      },
      patientCaseSheetHistory: {
        ...(existingRecord?.patientCaseSheetHistory || {}),
        historyOfPresentIllness: history.historyOfPresentIllness || "",
        historyOfPastIllness: history.historyOfPastIllness || "",
        familyHistory: history.familyHistory || "",
        personalHistory: history.personalHistory || "",
        treatmentHistory: history.treatmentHistory || "",
        socialOccupationalHistory: history.socialOccupationalHistory || "",
      },
      patientCaseSheetExamination: {
        ...(existingRecord?.patientCaseSheetExamination || {}),
        pulse: formatVital(examination.pulse, "bpm"),
        respiration: formatVital(examination.respiration, "/min"),
        temperature: formatVital(examination.temperature, "C"),
        bloodPressure: formatVital(examination.bloodPressure, "mmHg"),
        weight: formatVital(examination.weight, "kg"),
        height: formatVital(examination.height, "cm"),
        bmi: examination.bmi ? String(examination.bmi) : "",
        edema: examination.edema || "",
        scalp: examination.scalp || "",
        eyes: examination.eyes || "",
        nose: examination.nose || "",
        oralCavity: examination.oralCavity || "",
        tongue: examination.tongue || "",
        neckGlands: examination.neckGlands || "",
        nails: examination.nails || "",
        lymphNodes: examination.lymphNodes || "",
        pallorAnemia: examination.pallorAnemia || "",
        cyanosis: examination.cyanosis || "",
        extremities: examination.extremities || "",
        jaundice: examination.jaundice || "",
      },
      patientCaseSheetRespiratory: {
        ...(existingRecord?.patientCaseSheetRespiratory || {}),
        respiratoryRateRhythm: respiratory.respiratoryRateRhythm || "",
        shapeSymmetryChest: respiratory.shapeSymmetryChest || "",
        nose: respiratory.nose || "",
        crepitations: respiratory.crepitations || "",
        wheezing: respiratory.wheezing || "",
        pleuralRub: respiratory.pleuralRub || "",
      },
      patientCaseSheetCardiovascular: {
        ...(existingRecord?.patientCaseSheetCardiovascular || {}),
        pulseRate: formatVital(cardiovascular.pulseRate, "bpm"),
        rhythm: cardiovascular.rhythm || "",
        volume: cardiovascular.volume || "",
        bloodPressure: formatVital(cardiovascular.bloodPressure, "mmHg"),
        apexBeat: cardiovascular.apexBeat || "",
        pedalEdema: cardiovascular.pedalEdema || "",
        auscultation: cardiovascular.auscultation || "",
      },
      patientCaseSheetGastrointestinal: {
        ...(existingRecord?.patientCaseSheetGastrointestinal || {}),
        lips: gastrointestinal.lips || "",
        teeth: gastrointestinal.teeth || "",
        tongue: gastrointestinal.tongue || "",
        tonsils: gastrointestinal.tonsils || "",
        abdomenShape: gastrointestinal.abdomenShape || "",
        abdominalOrgans: gastrointestinal.abdominalOrgans || "",
        tenderness: gastrointestinal.tenderness || "",
        liver: gastrointestinal.liver || "",
        spleen: gastrointestinal.spleen || "",
        bowelSounds: gastrointestinal.bowelSounds || "",
      },
      patientCaseSheetCns: {
        ...(existingRecord?.patientCaseSheetCns || {}),
        mentalStatus: cns.mentalStatus || "",
        memory: cns.memory || "",
        orientation: cns.orientation || "",
        speech: cns.speech || "",
        generalBehavior: cns.generalBehavior || "",
        gait: cns.gait || "",
        cranialNerveExamination: cns.cranialNerveExamination || "",
        motorSensoryFindings: cns.motorSensoryFindings || "",
      },
      patientCaseSheetInvestigation: {
        ...(existingRecord?.patientCaseSheetInvestigation || {}),
        blood: investigation.blood || "",
        urine: investigation.urine || "",
        sputum: investigation.sputum || "",
        ecg: investigation.ecg || "",
        xray: investigation.xray || "",
        ct: investigation.ct || "",
        mri: investigation.mri || "",
        others: investigation.others || "",
      },
      patientCaseSheetDiagnosis: diagnosisList.map((item) => ({
        caseSheetId: item.caseSheetId || existingRecord?.id || 0,
        createdBy: String(item.createdBy || currentUserId || "").trim(),
        description:
          item.description ||
          item.diagnosisDesc ||
          item.primaryDiagnosis ||
          item.secondaryDiagnosis ||
          "",
        diagnosisCode: item.diagnosisCode || "",
        diagnosisNotes: item.diagnosisNotes || "",
        diagnosisType:
          item.diagnosisType && item.diagnosisType.toUpperCase() === "SECONDARY"
            ? "Secondary"
            : "Primary",
        id: item.id || 0,
        modifiedBy: String(currentUserId || item.modifiedBy || "").trim(),
        status: item.status !== undefined ? item.status : 1,
      })),
    };
  };

  // Submit Handler
  const handleSave = async (targetStatus = "DRAFT") => {
    if (!patientId || !String(patientId).trim()) {
      message.warning("Patient ID is missing. Please select or verify the patient.");
      return;
    }

    const payload = buildSavePayload(targetStatus);
    setIsSubmitting(true);

    try {
      let response;
      if (isUpdateMode) {
        response = await CaseSheetService.updateCaseSheet(payload);
        message.success("Patient Case Sheet updated successfully!");
      } else {
        response = await CaseSheetService.saveCaseSheet(payload);
        message.success(
          targetStatus === "DRAFT"
            ? "Patient Case Sheet draft saved successfully!"
            : "Patient Case Sheet saved successfully!"
        );
        setIsUpdateMode(true);
        if (response?.data) {
          setExistingRecord(response.data);
        }
      }
      if (onSave) {
        onSave(response?.data);
      }
    } catch (error) {
      console.error(`Error ${isUpdateMode ? "updating" : "saving"} Case Sheet:`, error);
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        `Failed to ${isUpdateMode ? "update" : "save"} Case Sheet. Please try again.`;
      message.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const navPills = [
    { key: "all", label: "All Sections" },
    { key: "complaints", label: "Complaints" },
    { key: "diagnosis", label: "Diagnosis" },
    { key: "history", label: "History" },
    { key: "vitals", label: "Vitals & Exam" },
    { key: "systems", label: "Systemic Exam" },
    { key: "investigations", label: "Investigations" },
  ];

  return (
    <div className="w-full bg-white rounded-2xl border border-[#e9edf5] shadow-xs my-3 overflow-hidden text-gray-800">
      {/* 1. Header Banner */}
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 via-white to-gray-50 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center text-[#008f9f] text-xl font-bold shadow-xs">
            <FaClipboardList className="text-[#008f9f]" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base md:text-lg font-bold text-gray-900">
                Inpatient Clinical Assessment & Case Sheet
              </h2>
              {/* <span className="text-[11px] font-semibold uppercase px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                {status}
              </span> */}
              {/* <span
                className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${
                  isUpdateMode
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                }`}
              >
                {isUpdateMode ? "Editing Existing Record" : "New Entry"}
              </span> */}
            </div>
            {/* <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
              <span>
                Patient: <strong className="text-gray-700">{patientName}</strong>
              </span>
             
              {admissionNo && (
                <span>
                  IP No: <code className="text-teal-700 font-semibold">{admissionNo}</code>
                </span>
              )}
            </p> */}
          </div>
        </div>

        <div className="flex items-center gap-2 self-end md:self-auto">
          {/* <button
            
            disabled={isSubmitting}
            onClick={() => {
              if (existingRecord) {
                applyCaseSheetData(existingRecord);
                message.info("Form reverted to loaded record.");
              } else {
                resetFormForDate();
                message.info("Form cleared.");
              }
            }}
            className="px-3.5 py-2 bg-white text-xs font-medium rounded-lg border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-1.5"
          >
            <FaSyncAlt className="text-gray-400 text-[11px]" />
            Reset
          </button> */}
          <button
            disabled={isSubmitting}
            onClick={() => handleSave("DRAFT")}
            className="px-5 py-2 text-xs bg-white font-semibold rounded-lg text-white hover:bg-[#007b89] transition-colors shadow-xs cursor-pointer flex items-center gap-2 disabled:opacity-50"
          >
            <FaSave className="text-xs" />
            {isSubmitting ? (
              <span>Saving...</span>
            ) : (
              <span>{isUpdateMode ? "Update Case Sheet" : "Save Case Sheet"}</span>
            )}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
              title="Close"
            >
              <FiX className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* 2. Date Selector Bar */}
      <div className="px-6 py-3.5 bg-gray-50/70 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-2">
          <FaCalendarAlt className="text-[#008f9f] text-sm" />
          <span className="text-xs font-semibold text-gray-700">
            Examination Date: <span className="text-teal-800">{selectedDateLabel}</span>
          </span>
          {isDateLoading && (
            <span className="text-xs text-[#008f9f] flex items-center gap-1 ml-2">
              <Spin size="small" /> Loading date records...
            </span>
          )}
        </div>

        {/* Week navigation cards */}
        <div className="flex items-center gap-2">
 

       

          <div className="flex items-center gap-1 ml-2">
            <button
              onClick={() => setWeekOffset((prev) => prev - 1)}
              className="w-7 h-7 bg-white flex items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-teal-50 text-gray-600 text-xs cursor-pointer"
              title="Previous week"
            >
              ‹
            </button>

            <div className="flex items-center gap-1">
              {weekDates.map((date) => {
                const isSelected = selectedDate === date.value;
                const isToday = today === date.value;

                return (
                  <button
                    key={date.value}
                    
                    onClick={() => setSelectedDate(date.value)}
                    className={`min-w-[42px] px-1 py-1 rounded-md border text-center transition-all cursor-pointer ${
                      isSelected
                        ? "bg-[#008f9f] text-white border-[#008f9f] font-semibold shadow-xs"
                        : "bg-white text-gray-700 border-gray-200 hover:border-teal-400 hover:bg-teal-50/50"
                    }`}
                  >
                    <span className="block text-[9px] uppercase leading-tight">{date.day}</span>
                    <span className="block text-xs font-bold leading-tight">{date.number}</span>
                    {isToday && (
                      <span
                        className={`block text-[7px] leading-tight ${
                          isSelected ? "text-teal-100" : "text-teal-600 font-bold"
                        }`}
                      >
                        ●
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setWeekOffset((prev) => prev + 1)}
              className="w-7 h-7 bg-white flex items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-teal-50 text-gray-600 text-xs cursor-pointer"
              title="Next week"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* 3. Section Quick-Jump Pills */}
      <div className="px-6 py-2 border-b border-gray-100 bg-white flex items-center gap-2 overflow-x-auto scrollHide">
        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider shrink-0 mr-1">
          Jump to:
        </span>
        {navPills.map((pill) => (
          <button
            key={pill.key}
            onClick={() => scrollToSection(pill.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              activeSection === pill.key
                ? "bg-teal-50 text-[#008f9f] border border-teal-200 font-semibold"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent"
            }`}
          >
            {pill.label}
          </button>
        ))}
      </div>

      {/* 4. Form Sections */}
      <div className="p-6 space-y-8 bg-white max-h-[75vh] overflow-y-auto">
        {/* Section 1: Main Complaints */}
        <div ref={sectionRefs.complaints} className="bg-gray-50/40 rounded-xl p-5 border border-gray-100">
          <div className={sectionHeader}>
            <MdOutlineSick className="text-[#008f9f] text-base" />
            <span>1. Chief Complaints</span>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2">Presenting Complaints</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={fieldLabel}>Presenting Complaint 1</label>
                  <input
                    type="text"
                    placeholder="e.g. Fever for 3 days"
                    value={complaints.presentingComplaint1}
                    onChange={(e) =>
                      setComplaints((prev) => ({ ...prev, presentingComplaint1: e.target.value }))
                    }
                    className={inputStyle}
                  />
                </div>
                <div>
                  <label className={fieldLabel}>Presenting Complaint 2</label>
                  <input
                    type="text"
                    placeholder="e.g. Productive cough"
                    value={complaints.presentingComplaint2}
                    onChange={(e) =>
                      setComplaints((prev) => ({ ...prev, presentingComplaint2: e.target.value }))
                    }
                    className={inputStyle}
                  />
                </div>
                <div>
                  <label className={fieldLabel}>Presenting Complaint 3</label>
                  <input
                    type="text"
                    placeholder="e.g. Generalized body ache"
                    value={complaints.presentingComplaint3}
                    onChange={(e) =>
                      setComplaints((prev) => ({ ...prev, presentingComplaint3: e.target.value }))
                    }
                    className={inputStyle}
                  />
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2">Associated Complaints</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={fieldLabel}>Associated Complaint 1</label>
                  <input
                    type="text"
                    placeholder="e.g. Mild nausea"
                    value={complaints.associatedComplaint1}
                    onChange={(e) =>
                      setComplaints((prev) => ({ ...prev, associatedComplaint1: e.target.value }))
                    }
                    className={inputStyle}
                  />
                </div>
                <div>
                  <label className={fieldLabel}>Associated Complaint 2</label>
                  <input
                    type="text"
                    placeholder="e.g. Loss of appetite"
                    value={complaints.associatedComplaint2}
                    onChange={(e) =>
                      setComplaints((prev) => ({ ...prev, associatedComplaint2: e.target.value }))
                    }
                    className={inputStyle}
                  />
                </div>
                <div>
                  <label className={fieldLabel}>Associated Complaint 3</label>
                  <input
                    type="text"
                    placeholder="e.g. Headache"
                    value={complaints.associatedComplaint3}
                    onChange={(e) =>
                      setComplaints((prev) => ({ ...prev, associatedComplaint3: e.target.value }))
                    }
                    className={inputStyle}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Provisional Diagnosis */}
        <div ref={sectionRefs.diagnosis} className="bg-gray-50/40 rounded-xl p-5 border border-gray-100">
          <div className={sectionHeader}>
            <FaStethoscope className="text-[#008f9f] text-base" />
            <span>2. Provisional Diagnosis</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={fieldLabel}>Specialty Code</label>
              <input
                type="text"
                placeholder="e.g. GENMED"
                value={specialtyCode}
                onChange={(e) => setSpecialtyCode(e.target.value)}
                className={inputStyle}
              />
            </div>
           
          </div>

          <div className="flex flex-col gap-3 bg-white p-4 rounded-xl border border-gray-200 mb-4 shadow-2xs">
            <div className="flex space-x-6 items-center">
              <span className="text-xs font-semibold text-gray-700">Diagnosis Type:</span>
              <label className="flex items-center space-x-2 text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="caseSheetDiagnosisType"
                  value="PRIMARY"
                  checked={selectedType === "PRIMARY"}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-4 h-4 text-[#189FAA] focus:ring-[#189FAA]"
                />
                <span className="text-xs text-[#333333] font-sans font-medium">
                  Primary
                </span>
              </label>
              <label className="flex items-center space-x-2 text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="caseSheetDiagnosisType"
                  value="SECONDARY"
                  checked={selectedType === "SECONDARY"}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-4 h-4 text-[#189FAA] focus:ring-[#189FAA]"
                />
                <span className="text-xs text-[#333333] font-sans font-medium">
                  Secondary
                </span>
              </label>
            </div>

            <div className="flex flex-col md:flex-row items-center w-full gap-3">
              <div className="w-full md:w-5/12">
                <Select
                  showSearch
                  placeholder="Search Disease Condition"
                  className="h-10 w-full"
                  onSearch={handleIcdDrescriptionSearch}
                  onChange={(value, option) => setSelectedCode(option?.data || null)}
                  value={selectedCode?.description || undefined}
                  options={diagnosticList?.map((des) => ({
                    label: des.description,
                    value: des.code,
                    data: des,
                  }))}
                  filterOption={(input, option) =>
                    option &&
                    option.label &&
                    option.label.toUpperCase().indexOf(input.toUpperCase()) >= 0
                  }
                  allowClear
                  notFoundContent={null}
                />
              </div>
              <div className="w-full md:w-3/12">
                <Select
                  showSearch
                  placeholder="Search ICD Code"
                  className="h-10 w-full"
                  onSearch={handleIcdCodeSearch}
                  onChange={(value, option) => setSelectedCode(option?.data || null)}
                  value={selectedCode?.code || undefined}
                  options={diagnosticList?.map((des) => ({
                    label: des.code,
                    value: des.code,
                    data: des,
                  }))}
                  filterOption={(input, option) =>
                    option &&
                    option.label &&
                    option.label.toUpperCase().indexOf(input.toUpperCase()) >= 0
                  }
                  allowClear
                  notFoundContent={null}
                />
              </div>
              <div className="w-full md:w-3/12">
                <input
                  type="text"
                  placeholder="Clinical notes / remarks (optional)"
                  value={diagnosisNotesInput}
                  onChange={(e) => setDiagnosisNotesInput(e.target.value)}
                  className={inputStyle}
                />
              </div>
              <button
                type="button"
                onClick={handleAddDiagnosis}
                className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-teal-50 text-teal-700 hover:bg-teal-100 rounded-md transition border border-teal-200 cursor-pointer"
                title="Add Diagnosis"
              >
                <FiPlus className="w-5 h-5" />
              </button>
            </div>
          </div>

          {diagnosisList && diagnosisList.length > 0 ? (
            <div className="flex flex-col w-full border border-gray-300 rounded-lg shadow-2xs overflow-hidden bg-white">
              <div className="flex w-full bg-[#F5FEFA] text-[#333333] font-sans font-medium text-xs">
                <div className="p-2 border-r border-[#D2E1E2] text-center w-[15%]">
                  Type
                </div>
                <div className="p-2 border-r border-[#D2E1E2] text-left w-[40%]">
                  Disease Condition
                </div>
                <div className="p-2 border-r border-[#D2E1E2] text-center w-[15%]">
                  ICD-10 Code
                </div>
                <div className="p-2 border-r border-[#D2E1E2] text-left w-[20%]">
                  Clinical Notes
                </div>
                <div className="p-2 text-center w-[10%]">
                  Action
                </div>
              </div>

              <div className="w-full divide-y divide-gray-200">
                {diagnosisList.map((item, index) => (
                  <div
                    key={index}
                    className="flex w-full text-teal-700 font-sans font-medium text-xs bg-white hover:bg-gray-50 items-center"
                  >
                    <div className="p-2 border-r border-gray-200 text-center w-[15%] font-bold">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${
                          (item.diagnosisType || "").toUpperCase() === "PRIMARY"
                            ? "bg-teal-50 text-teal-700 border border-teal-200"
                            : "bg-blue-50 text-blue-700 border border-blue-200"
                        }`}
                      >
                        {item.diagnosisType || (item.primaryDiagnosis ? "Primary" : "Secondary")}
                      </span>
                    </div>
                    <div className="p-2 border-r border-gray-200 text-left w-[40%] text-gray-700">
                      {item.description || item.diagnosisDesc || item.primaryDiagnosis || item.secondaryDiagnosis || "N/A"}
                    </div>
                    <div className="p-2 border-r border-gray-200 text-center w-[15%] text-gray-700 font-mono">
                      {item.diagnosisCode || "N/A"}
                    </div>
                    <div
                      className="p-2 border-r border-gray-200 text-left w-[20%] text-gray-600 truncate"
                      title={item.diagnosisNotes}
                    >
                      {item.diagnosisNotes || "-"}
                    </div>
                    <div className="p-2 flex justify-center items-center text-center w-[10%]">
                      <button
                        type="button"
                        onClick={() => handleRemoveDiagnosis(index)}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition cursor-pointer"
                        title="Remove diagnosis"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-4 border border-dashed border-gray-200 rounded-lg text-center text-gray-400 text-xs">
              No diagnoses added yet. Search and select disease condition or ICD code above to add.
            </div>
          )}
        </div>

        {/* Section 3: History */}
        <div ref={sectionRefs.history} className="bg-gray-50/40 rounded-xl p-5 border border-gray-100">
          <div className={sectionHeader}>
            <FaHistory className="text-[#008f9f] text-base" />
            <span>3. Clinical History</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={fieldLabel}>History of Present Illness (HOPI)</label>
              <textarea
                rows={3}
                placeholder="Onset, duration, progression, aggravating and relieving factors..."
                value={history.historyOfPresentIllness}
                onChange={(e) =>
                  setHistory((prev) => ({ ...prev, historyOfPresentIllness: e.target.value }))
                }
                className={textareaStyle}
              />
            </div>
            <div>
              <label className={fieldLabel}>History of Past Illness</label>
              <textarea
                rows={3}
                placeholder="Previous hospitalizations, surgical history, chronic conditions..."
                value={history.historyOfPastIllness}
                onChange={(e) =>
                  setHistory((prev) => ({ ...prev, historyOfPastIllness: e.target.value }))
                }
                className={textareaStyle}
              />
            </div>
            <div>
              <label className={fieldLabel}>Family History</label>
              <textarea
                rows={2}
                placeholder="Family history of DM, HTN, CAD, malignancies..."
                value={history.familyHistory}
                onChange={(e) =>
                  setHistory((prev) => ({ ...prev, familyHistory: e.target.value }))
                }
                className={textareaStyle}
              />
            </div>
            <div>
              <label className={fieldLabel}>Personal History</label>
              <textarea
                rows={2}
                placeholder="Dietary habits, bowel and bladder, sleep, addictions..."
                value={history.personalHistory}
                onChange={(e) =>
                  setHistory((prev) => ({ ...prev, personalHistory: e.target.value }))
                }
                className={textareaStyle}
              />
            </div>
            <div>
              <label className={fieldLabel}>Treatment History</label>
              <textarea
                rows={2}
                placeholder="Current medications, prior drug treatments..."
                value={history.treatmentHistory}
                onChange={(e) =>
                  setHistory((prev) => ({ ...prev, treatmentHistory: e.target.value }))
                }
                className={textareaStyle}
              />
            </div>
            <div>
              <label className={fieldLabel}>Social & Occupational History</label>
              <textarea
                rows={2}
                placeholder="Occupation, environmental exposures, socioeconomic factors..."
                value={history.socialOccupationalHistory}
                onChange={(e) =>
                  setHistory((prev) => ({ ...prev, socialOccupationalHistory: e.target.value }))
                }
                className={textareaStyle}
              />
            </div>
          </div>
        </div>

        {/* Section 4: Physical Examination & Vitals */}
        <div ref={sectionRefs.vitals} className="bg-gray-50/40 rounded-xl p-5 border border-gray-100">
          <div className={sectionHeader}>
            <FaHeartbeat className="text-[#008f9f] text-base" />
            <span>4. Physical Examination & Vitals</span>
          </div>

          {/* Vitals Highlights Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-2xs">
              <span className="text-[11px] text-gray-500 font-medium block">Pulse Rate</span>
              <div className="flex items-center gap-1 mt-1">
                <input
                  type="text"
                  placeholder="72"
                  value={examination.pulse}
                  onChange={(e) => updateExaminationField("pulse", e.target.value)}
                  className="w-16 font-bold text-sm text-gray-800 outline-none border-b border-gray-200 focus:border-teal-500"
                />
                <span className="text-[11px] text-gray-400">bpm</span>
              </div>
            </div>

            <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-2xs">
              <span className="text-[11px] text-gray-500 font-medium block">Respiration</span>
              <div className="flex items-center gap-1 mt-1">
                <input
                  type="text"
                  placeholder="18"
                  value={examination.respiration}
                  onChange={(e) => updateExaminationField("respiration", e.target.value)}
                  className="w-16 font-bold text-sm text-gray-800 outline-none border-b border-gray-200 focus:border-teal-500"
                />
                <span className="text-[11px] text-gray-400">/min</span>
              </div>
            </div>

            <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-2xs">
              <span className="text-[11px] text-gray-500 font-medium block">Temperature</span>
              <div className="flex items-center gap-1 mt-1">
                <input
                  type="text"
                  placeholder="37"
                  value={examination.temperature}
                  onChange={(e) => updateExaminationField("temperature", e.target.value)}
                  className="w-16 font-bold text-sm text-gray-800 outline-none border-b border-gray-200 focus:border-teal-500"
                />
                <span className="text-[11px] text-gray-400">°C</span>
              </div>
            </div>

            <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-2xs">
              <span className="text-[11px] text-gray-500 font-medium block">Blood Pressure</span>
              <div className="flex items-center gap-1 mt-1">
                <input
                  type="text"
                  placeholder="120/80"
                  value={examination.bloodPressure}
                  onChange={(e) => updateExaminationField("bloodPressure", e.target.value)}
                  className="w-20 font-bold text-sm text-gray-800 outline-none border-b border-gray-200 focus:border-teal-500"
                />
                <span className="text-[11px] text-gray-400">mmHg</span>
              </div>
            </div>

            <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-2xs">
              <span className="text-[11px] text-gray-500 font-medium block">Weight</span>
              <div className="flex items-center gap-1 mt-1">
                <input
                  type="text"
                  placeholder="65"
                  value={examination.weight}
                  onChange={(e) => updateExaminationField("weight", e.target.value)}
                  className="w-16 font-bold text-sm text-gray-800 outline-none border-b border-gray-200 focus:border-teal-500"
                />
                <span className="text-[11px] text-gray-400">kg</span>
              </div>
            </div>

            <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-2xs">
              <span className="text-[11px] text-gray-500 font-medium block">Height</span>
              <div className="flex items-center gap-1 mt-1">
                <input
                  type="text"
                  placeholder="170"
                  value={examination.height}
                  onChange={(e) => updateExaminationField("height", e.target.value)}
                  className="w-16 font-bold text-sm text-gray-800 outline-none border-b border-gray-200 focus:border-teal-500"
                />
                <span className="text-[11px] text-gray-400">cm</span>
              </div>
            </div>

            <div className="bg-teal-50/60 p-3 rounded-xl border border-teal-100 shadow-2xs">
              <span className="text-[11px] text-teal-700 font-medium block">BMI (Auto)</span>
              <div className="flex items-center gap-1 mt-1">
                <span className="font-bold text-sm text-teal-900">
                  {examination.bmi || "--"}
                </span>
                <span className="text-[10px] text-teal-600">kg/m²</span>
              </div>
            </div>
          </div>

          {/* General Examination Findings Grid */}
          <p className="text-xs font-semibold text-gray-700 mb-2">General Physical Findings</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: "pallorAnemia", label: "Pallor / Anemia" },
              { key: "jaundice", label: "Jaundice / Icterus" },
              { key: "cyanosis", label: "Cyanosis" },
              { key: "edema", label: "Edema / Clubbing" },
              { key: "lymphNodes", label: "Lymphadenopathy" },
              { key: "nails", label: "Nails" },
              { key: "eyes", label: "Eyes / Pupils" },
              { key: "oralCavity", label: "Oral Cavity" },
              { key: "tongue", label: "Tongue" },
              { key: "neckGlands", label: "Neck & Thyroid" },
              { key: "scalp", label: "Scalp" },
              { key: "extremities", label: "Extremities" },
            ].map((item) => (
              <div key={item.key}>
                <label className={fieldLabel}>{item.label}</label>
                <input
                  type="text"
                  placeholder="Normal / Nil"
                  value={examination[item.key] || ""}
                  onChange={(e) => updateExaminationField(item.key, e.target.value)}
                  className={inputStyle}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Section 5: Systemic Examination */}
        <div ref={sectionRefs.systems} className="space-y-6">
          {/* Respiratory */}
          <div className="bg-gray-50/40 rounded-xl p-5 border border-gray-100">
            <div className={sectionHeader}>
              <FaLungs className="text-[#008f9f] text-base" />
              <span>5. Respiratory System Examination</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className={fieldLabel}>Rate & Rhythm</label>
                <input
                  type="text"
                  placeholder="Regular, abdominothoracic"
                  value={respiratory.respiratoryRateRhythm}
                  onChange={(e) =>
                    setRespiratory((prev) => ({ ...prev, respiratoryRateRhythm: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Shape & Symmetry of Chest</label>
                <input
                  type="text"
                  placeholder="Symmetrical, normal shape"
                  value={respiratory.shapeSymmetryChest}
                  onChange={(e) =>
                    setRespiratory((prev) => ({ ...prev, shapeSymmetryChest: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Nose / Upper Airway</label>
                <input
                  type="text"
                  placeholder="Clear, no discharge"
                  value={respiratory.nose}
                  onChange={(e) =>
                    setRespiratory((prev) => ({ ...prev, nose: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Crepitations / Crackles</label>
                <input
                  type="text"
                  placeholder="Nil / Present (location)"
                  value={respiratory.crepitations}
                  onChange={(e) =>
                    setRespiratory((prev) => ({ ...prev, crepitations: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Wheezing / Rhonchi</label>
                <input
                  type="text"
                  placeholder="Nil / Expiratory wheeze"
                  value={respiratory.wheezing}
                  onChange={(e) =>
                    setRespiratory((prev) => ({ ...prev, wheezing: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Pleural Rub</label>
                <input
                  type="text"
                  placeholder="Absent"
                  value={respiratory.pleuralRub}
                  onChange={(e) =>
                    setRespiratory((prev) => ({ ...prev, pleuralRub: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Cardiovascular */}
          <div className="bg-gray-50/40 rounded-xl p-5 border border-gray-100">
            <div className={sectionHeader}>
              <FaHeartbeat className="text-[#008f9f] text-base" />
              <span>6. Cardiovascular System Examination</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className={fieldLabel}>Pulse Rate & Character</label>
                <input
                  type="text"
                  placeholder="e.g. 74 bpm, regular"
                  value={cardiovascular.pulseRate}
                  onChange={(e) =>
                    setCardiovascular((prev) => ({ ...prev, pulseRate: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Heart Rhythm</label>
                <input
                  type="text"
                  placeholder="Regular sinus rhythm"
                  value={cardiovascular.rhythm}
                  onChange={(e) =>
                    setCardiovascular((prev) => ({ ...prev, rhythm: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Apex Beat</label>
                <input
                  type="text"
                  placeholder="5th intercostal space, midclavicular line"
                  value={cardiovascular.apexBeat}
                  onChange={(e) =>
                    setCardiovascular((prev) => ({ ...prev, apexBeat: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Pedal Edema</label>
                <input
                  type="text"
                  placeholder="None / Pitting / Non-pitting"
                  value={cardiovascular.pedalEdema}
                  onChange={(e) =>
                    setCardiovascular((prev) => ({ ...prev, pedalEdema: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={fieldLabel}>Auscultation (Heart Sounds / Murmurs)</label>
                <input
                  type="text"
                  placeholder="S1, S2 heard, no murmurs or gallops"
                  value={cardiovascular.auscultation}
                  onChange={(e) =>
                    setCardiovascular((prev) => ({ ...prev, auscultation: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Gastrointestinal */}
          <div className="bg-gray-50/40 rounded-xl p-5 border border-gray-100">
            <div className={sectionHeader}>
              <GiStomach className="text-[#008f9f] text-base" />
              <span>7. Gastrointestinal System Examination</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className={fieldLabel}>Abdomen Shape & Contour</label>
                <input
                  type="text"
                  placeholder="Scaphoid / Flat / Distended"
                  value={gastrointestinal.abdomenShape}
                  onChange={(e) =>
                    setGastrointestinal((prev) => ({ ...prev, abdomenShape: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Tenderness / Guarding</label>
                <input
                  type="text"
                  placeholder="Soft, non-tender, no guarding"
                  value={gastrointestinal.tenderness}
                  onChange={(e) =>
                    setGastrointestinal((prev) => ({ ...prev, tenderness: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Liver</label>
                <input
                  type="text"
                  placeholder="Not palpable"
                  value={gastrointestinal.liver}
                  onChange={(e) =>
                    setGastrointestinal((prev) => ({ ...prev, liver: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Spleen</label>
                <input
                  type="text"
                  placeholder="Not palpable"
                  value={gastrointestinal.spleen}
                  onChange={(e) =>
                    setGastrointestinal((prev) => ({ ...prev, spleen: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Bowel Sounds</label>
                <input
                  type="text"
                  placeholder="Normal active bowel sounds"
                  value={gastrointestinal.bowelSounds}
                  onChange={(e) =>
                    setGastrointestinal((prev) => ({ ...prev, bowelSounds: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Lips, Teeth & Tonsils</label>
                <input
                  type="text"
                  placeholder="Normal, no tonsillar hypertrophy"
                  value={gastrointestinal.tonsils}
                  onChange={(e) =>
                    setGastrointestinal((prev) => ({ ...prev, tonsils: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Central Nervous System */}
          <div className="bg-gray-50/40 rounded-xl p-5 border border-gray-100">
            <div className={sectionHeader}>
              <FaBrain className="text-[#008f9f] text-base" />
              <span>8. Central Nervous System (CNS) Examination</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className={fieldLabel}>Mental Status & Consciousness</label>
                <input
                  type="text"
                  placeholder="Conscious, alert, oriented"
                  value={cns.mentalStatus}
                  onChange={(e) => setCns((prev) => ({ ...prev, mentalStatus: e.target.value }))}
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Orientation (Time, Place, Person)</label>
                <input
                  type="text"
                  placeholder="Oriented x3"
                  value={cns.orientation}
                  onChange={(e) => setCns((prev) => ({ ...prev, orientation: e.target.value }))}
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Speech</label>
                <input
                  type="text"
                  placeholder="Coherent, clear, fluent"
                  value={cns.speech}
                  onChange={(e) => setCns((prev) => ({ ...prev, speech: e.target.value }))}
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>General Behavior & Memory</label>
                <input
                  type="text"
                  placeholder="Intact, cooperative"
                  value={cns.generalBehavior}
                  onChange={(e) =>
                    setCns((prev) => ({ ...prev, generalBehavior: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Gait & Posture</label>
                <input
                  type="text"
                  placeholder="Normal steady gait"
                  value={cns.gait}
                  onChange={(e) => setCns((prev) => ({ ...prev, gait: e.target.value }))}
                  className={inputStyle}
                />
              </div>
              <div>
                <label className={fieldLabel}>Cranial Nerves (I-XII)</label>
                <input
                  type="text"
                  placeholder="Grossly intact"
                  value={cns.cranialNerveExamination}
                  onChange={(e) =>
                    setCns((prev) => ({ ...prev, cranialNerveExamination: e.target.value }))
                  }
                  className={inputStyle}
                />
              </div>
            </div>
            <div>
              <label className={fieldLabel}>Motor System & Sensory System Findings</label>
              <textarea
                rows={2}
                placeholder="Bulk, tone, power 5/5 in all 4 limbs, sensory sensations intact, reflexes normal..."
                value={cns.motorSensoryFindings}
                onChange={(e) =>
                  setCns((prev) => ({ ...prev, motorSensoryFindings: e.target.value }))
                }
                className={textareaStyle}
              />
            </div>
          </div>
        </div>

        {/* Section 6: Investigations Advised */}
        <div ref={sectionRefs.investigations} className="bg-gray-50/40 rounded-xl p-5 border border-gray-100">
          <div className={sectionHeader}>
            <FaFlask className="text-[#008f9f] text-base" />
            <span>9. Investigations Advised</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { key: "blood", label: "Blood Tests", placeholder: "e.g. CBC, LFT, RFT, Electrolytes" },
              { key: "urine", label: "Urine Routine & Micro", placeholder: "e.g. Routine & Microscopic" },
              { key: "sputum", label: "Sputum Examination", placeholder: "e.g. Gram stain, AFB" },
              { key: "ecg", label: "ECG", placeholder: "e.g. 12-lead ECG" },
              { key: "xray", label: "X-Ray", placeholder: "e.g. Chest X-Ray PA view" },
              { key: "ct", label: "CT Scan", placeholder: "e.g. HRCT Chest / NCCT Abdomen" },
              { key: "mri", label: "MRI", placeholder: "e.g. MRI Brain" },
              { key: "others", label: "Other Diagnostics", placeholder: "e.g. USG Abdomen, 2D Echo" },
            ].map((item) => (
              <div key={item.key}>
                <label className={fieldLabel}>{item.label}</label>
                <textarea
                  rows={2}
                  placeholder={item.placeholder}
                  value={investigation[item.key]}
                  onChange={(e) =>
                    setInvestigation((prev) => ({ ...prev, [item.key]: e.target.value }))
                  }
                  className={textareaStyle}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 5. Sticky Footer Bar */}
      <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>Active Examination Date:</span>
          <span className="font-semibold text-gray-800">{selectedDate}</span>
          <span className="text-gray-300">|</span>
          <span>Status:</span>
          <span className="font-semibold text-teal-700 uppercase">{status}</span>
        </div>

        <div className="flex items-center justify-end gap-3">
          {onClose && (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onClose}
              className="px-4 py-2 bg-white text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            disabled={isSubmitting}
            onClick={() => {
              if (existingRecord) {
                applyCaseSheetData(existingRecord);
                message.info("Form reverted to loaded record.");
              } else {
                resetFormForDate();
                message.info("Form reset.");
              }
            }}
            className="px-4 py-2 bg-white text-xs font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50"
          >
            Reset Form
          </button>

          <button
            disabled={isSubmitting}
            onClick={() => handleSave("DRAFT")}
            className="px-6 py-2 bg-white text-xs font-semibold rounded-lg bg-[#008f9f] text-white hover:bg-[#007b89] transition-colors shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
          >
            {isSubmitting ? (
              <>
                <Spin size="small" />
                <span>Saving...</span>
              </>
            ) : (
              <span>{isUpdateMode ? "Update Case Sheet" : "Save Case Sheet"}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
